import React, { useEffect, useRef } from "react";

// 노선유형별 색상 반환 함수
function getRouteTypeColor(routeType) {
  switch (routeType) {
    case '공항': return '#00bfff'; // 하늘색
    case '마을': return '#a0d468'; // 연두색
    case '일반': return '#32cd32'; // 라임색
    case '간선': return '#3498db'; // 파란색
    case '지선': return '#27ae60'; // 초록색
    case '순환': return '#f1c40f'; // 노란색
    case '광역': return '#e74c3c';// 빨간색
    case '급행': return '#ff7f00';//주황
    case '직행좌석': return '#e60012'; // 빨간색
    case '좌석': return '#9999ff';

    case '농어촌(일반)': return '#daa520'; // 노란색
    case '인천': return '#32cd32'; // 라임색
    case '경기': return '#32cd32'; // 라임색
    case '관광': return '#8b00ff'; // 보라색

    default: return '#000'; // 기본 검정
  }
}

// 15자마다 단어 기준 줄바꿈
function wrapTextByWord(text, maxLen) {
  if (!text) return '';
  const words = text.split(' ');
  let lines = [];
  let current = '';
  for (let word of words) {
    if ((current + (current ? ' ' : '') + word).length > maxLen) {
      if (current) lines.push(current);
      current = word;
    } else {
      current += (current ? ' ' : '') + word;
    }
  }
  if (current) lines.push(current);
  return lines.join('<br>');
}

// Kakao Map SDK 동적 로딩 (Promise 없이, autoload=false, maps.load 콜백에서만 지도 초기화)
let kakaoMapScriptLoaded = false;
function ensureKakaoMapScript(callback) {
  if (window.kakao && window.kakao.maps && typeof window.kakao.maps.LatLng === 'function') {
    window.kakao.maps.load(callback);
    return;
  }
  if (kakaoMapScriptLoaded) {
    // 이미 script 태그가 있으나 아직 로드 중이면 onload에서만 콜백
    return;
  }
  kakaoMapScriptLoaded = true;
  const script = document.createElement("script");
  script.src = "//dapi.kakao.com/v2/maps/sdk.js?appkey=fa0a037f34e1a5e2e4d96144d510f87c&libraries=services&autoload=false";
  script.async = true;
  script.onload = function() {
    window.kakao.maps.load(callback);
  };
  document.head.appendChild(script);
}

export default function MapModal({ open, onClose, routes, labels = [], zoom = 1, showAllMarkers = true, title = "지도" }) {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let map, polylines = [], markers = [], infoWindows = [], arrows = [];
    let isUnmounted = false;

    ensureKakaoMapScript(() => {
      if (!mapRef.current) return;
      map = new window.kakao.maps.Map(mapRef.current, {
        center: new window.kakao.maps.LatLng(37.5665, 126.9780),
        level: 7
      });

      // 점(x0, y0)과 선분 (x1, y1)-(x2, y2) 사이의 최단 거리
      function pointToSegmentDistance(x0, y0, x1, y1, x2, y2) {
        const A = x0 - x1;
        const B = y0 - y1;
        const C = x2 - x1;
        const D = y2 - y1;
        const dot = A * C + B * D;
        const len_sq = C * C + D * D;
        let param = -1;
        if (len_sq !== 0) param = dot / len_sq;
        let xx, yy;
        if (param < 0) {
          xx = x1;
          yy = y1;
        } else if (param > 1) {
          xx = x2;
          yy = y2;
        } else {
          xx = x1 + param * C;
          yy = y1 + param * D;
        }
        const dx = x0 - xx;
        const dy = y0 - yy;
        return Math.sqrt(dx * dx + dy * dy);
      }

      // 점-선분 거리 기준 top3 인덱스 구하기
      function getTop3Indices(lat, lng, linePath) {
        const arr = [];
        for (let j = 1; j < linePath.length; j++) {
          const dist = pointToSegmentDistance(
            lat, lng,
            linePath[j-1].getLat(), linePath[j-1].getLng(),
            linePath[j].getLat(), linePath[j].getLng()
          );
          arr.push({ idx: j, dist });
        }
        arr.sort((a, b) => a.dist - b.dist);
        return arr.slice(0, 5).map(e => e.idx);
      }

      // 두 점의 유클리드 거리(직선 거리)
      function getDistance(lat1, lng1, lat2, lng2) {
        return Math.sqrt(Math.pow(lat1 - lat2, 2) + Math.pow(lng1 - lng2, 2));
      }

      // drawRoutePolylines
      (async () => {
        for (let i = 0; i < routes.length; i++) {
          let route = routes[i];
          let color = getRouteTypeColor(route.routeType);

          // API 호출 및 경로 좌표 계산
          let linePath = [];
          let isSeoulRoute = false, isGGBRoute = false;
          try {
            isSeoulRoute = !/^[A-Za-z]{3}/.test(route.routeId);
            isGGBRoute = /^GGB/.test(route.routeId);
            if (isSeoulRoute) {
              // 서울시 API
              const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
              const url = `/busapi/api/rest/busRouteInfo/getRoutePath?serviceKey=${serviceKey}&busRouteId=${route.routeId}`;
              const res = await fetch(url);
              const xmlText = await res.text();
              const parser = new window.DOMParser();
              const xml = parser.parseFromString(xmlText, "text/xml");
              const items = xml.querySelectorAll("itemList");
              if (items.length > 0) {
                linePath = Array.from(items).map(item =>
                  new window.kakao.maps.LatLng(
                    Number(item.querySelector("gpsY")?.textContent),
                    Number(item.querySelector("gpsX")?.textContent)
                  )
                );
              }
            } else if (isGGBRoute) {
              // 경기(GGB) API
              const routeId = route.routeId.replace(/^[A-Za-z]{3}/, "");
              const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
              const url = `https://apis.data.go.kr/6410000/busrouteservice/v2/getBusRouteLineListv2?serviceKey=${serviceKey}&routeId=${routeId}&format=json`;
              const res = await fetch(url);
              const data = await res.json();
              const points = data.response?.msgBody?.busRouteLineList;
              if (points) {
                const path = Array.isArray(points) ? points : [points];
                linePath = path.map(p => new window.kakao.maps.LatLng(Number(p.y), Number(p.x)));
              }
            } else {
              // 기타(국토부) API
              const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
              const url = `https://apis.data.go.kr/1613000/BusRouteInfoInqireService/getRouteAcctoThrghSttnList?serviceKey=${serviceKey}&pageNo=1&numOfRows=200&_type=json&cityCode=${route.cityCode}&routeId=${route.routeId}`;
              const res = await fetch(url);
              const data = await res.json();
              const items = data?.response?.body?.items?.item;
              if (items) {
                const stops = Array.isArray(items) ? items : [items];
                linePath = stops.map(stop => new window.kakao.maps.LatLng(Number(stop.gpslati), Number(stop.gpslong)));
              }
            }
          } catch (e) {}

          if (linePath.length > 0) {
            // 승하차지 인덱스 계산
            const boardingLat = route.boarding?.lat;
            const boardingLng = route.boarding?.lng;
            const alightingLat = route.alighting?.lat;
            const alightingLng = route.alighting?.lng;

            let boardingIdx = -1, alightingIdx = -1;
            if (isSeoulRoute || isGGBRoute) {
              // 기존 방식 (top3 조합)
              const boardingTop10 = getTop3Indices(boardingLat, boardingLng, linePath);
              const alightingTop10 = getTop3Indices(alightingLat, alightingLng, linePath);
              let pairs = [];
              for (let b of boardingTop10) {
                for (let a of alightingTop10) {
                  const diff = a - b;
                  if (diff > 0) {
                    pairs.push({ b, a, diff });
                  }
                }
              }
              pairs.sort((x, y) => x.diff - y.diff);
              let idx = 0;
              while (
                idx + 2 < pairs.length &&
                (pairs[idx + 2].diff - pairs[idx].diff < 3)
              ) {
                idx++;
              }
              const bestBoardingIdx = pairs[idx]?.b ?? boardingTop10[0];
              const bestAlightingIdx = pairs[idx]?.a ?? alightingTop10[0];
              boardingIdx = bestBoardingIdx;
              alightingIdx = bestAlightingIdx;
            } else {
              // 기타(국토부): 같은 좌표가 여러 개일 수 있으므로, 최단거리 인덱스 모두 구함
              function getAllMinIndices(arr, targetLat, targetLng) {
                let minDist = Infinity;
                let indices = [];
                for (let j = 0; j < arr.length; j++) {
                  const dist = getDistance(arr[j].getLat(), arr[j].getLng(), targetLat, targetLng);
                  if (dist < minDist - 1e-10) { // float 오차 방지
                    minDist = dist;
                    indices = [j];
                  } else if (Math.abs(dist - minDist) < 1e-10) {
                    indices.push(j);
                  }
                }
                return indices;
              }
              const boardingIndices = getAllMinIndices(linePath, boardingLat, boardingLng);
              const alightingIndices = getAllMinIndices(linePath, alightingLat, alightingLng);
              // 승차 < 하차 쌍 중 인덱스 차이가 가장 작은 쌍 선택
              let minDiff = Infinity;
              let bestBoardingIdx = boardingIndices[0], bestAlightingIdx = alightingIndices[0];
              for (let b of boardingIndices) {
                for (let a of alightingIndices) {
                  if (a > b && (a - b) < minDiff) {
                    minDiff = a - b;
                    bestBoardingIdx = b;
                    bestAlightingIdx = a;
                  }
                }
              }
              boardingIdx = bestBoardingIdx;
              alightingIdx = bestAlightingIdx;
            }

            // 승하차지 사이의 경로만 추출
            let routePath = [];
            if (boardingIdx !== -1 && alightingIdx !== -1) {
              const startIdx = Math.min(boardingIdx, alightingIdx);
              const endIdx = Math.max(boardingIdx, alightingIdx);
              routePath = linePath.slice(startIdx, endIdx + 1);
            } else {
              routePath = linePath; // 못 찾으면 전체 경로
            }

            // 폴리라인 생성
            const polyline = new window.kakao.maps.Polyline({
              path: routePath,
              strokeWeight: 5,
              strokeColor: color,
              strokeOpacity: 0.8,
              strokeStyle: 'solid'
            });
            polyline.setMap(map);
            polylines.push(polyline);

            // --- 진행 방향 화살표 추가 (중간 구간마다) ---
            if (routePath.length > 1) {
              // 화살표 생성 함수
              const createArrow = (arrowStart, arrowEnd) => {
                const dx = arrowEnd.getLng() - arrowStart.getLng();
                const dy = arrowEnd.getLat() - arrowStart.getLat();
                const angle = Math.atan2(dy, dx);
                const arrowLength = 0.002;
                const arrowAngle = Math.PI / 5;
                
                const tip1 = new window.kakao.maps.LatLng(
                  arrowEnd.getLat() - arrowLength * Math.sin(angle - arrowAngle),
                  arrowEnd.getLng() - arrowLength * Math.cos(angle - arrowAngle)
                );
                const tip2 = new window.kakao.maps.LatLng(
                  arrowEnd.getLat() - arrowLength * Math.sin(angle + arrowAngle),
                  arrowEnd.getLng() - arrowLength * Math.cos(angle + arrowAngle)
                );
                
                const arrow1 = new window.kakao.maps.Polyline({
                  path: [tip1, arrowEnd],
                  strokeWeight: 5,
                  strokeColor: color,
                  strokeOpacity: 0.4,
                  strokeStyle: 'shortdashdot'
                });
                arrow1.setMap(map);
                const arrow2 = new window.kakao.maps.Polyline({
                  path: [tip2, arrowEnd],
                  strokeWeight: 5,
                  strokeColor: color,
                  strokeOpacity: 0.4,
                  strokeStyle: 'shortdashdot'
                });
                arrow2.setMap(map);
                arrows.push(arrow1, arrow2);
              };
              
              // 중간 화살표 개수: 기본 3개, 100개마다 1개씩 추가
              const baseArrows = 2;
              const extraArrows = Math.floor(Math.max(0, routePath.length - 1) / 100);
              const totalArrows = baseArrows + extraArrows;
              if (routePath.length > 1) {
                for (let k = 1; k <= totalArrows; k++) {
                  const idx = Math.round((k * (routePath.length - 1)) / (totalArrows + 1));
                  if (idx > 0 && idx < routePath.length - 1) {
                    createArrow(routePath[idx - 1], routePath[idx]);
                  }
                }
              }
              
              // 마지막 화살표
              createArrow(routePath[routePath.length - 2], routePath[routePath.length - 1]);
            }

            // 승하차지 마커 및 InfoWindow (showAllMarkers가 true일 때만 생성)
            if (showAllMarkers) {
              if (boardingLat && boardingLng) {
                const marker = new window.kakao.maps.Marker({
                  position: new window.kakao.maps.LatLng(boardingLat, boardingLng),
                  map: map
                });
                // 순번 오버레이
                const markerIndex = i + 1;
                const overlayContent = `
                  <div style="
                    background: #3498db;
                    color: #fff;
                    font-weight: bold;
                    border-radius: 50%;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    border: 2px solid #fff;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                    position: absolute;
                    left: -16px; top: -40px;
                    z-index: ${10000 - markerIndex*10};
                    pointer-events: none;
                  ">${markerIndex}</div>
                `;
                const indexOverlay = new window.kakao.maps.CustomOverlay({
                  position: new window.kakao.maps.LatLng(boardingLat, boardingLng),
                  content: overlayContent,
                  yAnchor: 1
                });
                indexOverlay.setMap(map);
                markers.push({ marker, overlay: indexOverlay });

                const routeLabel = labels && labels[i] ? labels[i] : '';
                let boardingLabel = routeLabel;
                if (routeLabel.includes('//')) {
                  [boardingLabel] = routeLabel.split('//', 2);
                }
                const boardingLabelWrapped = wrapTextByWord(boardingLabel, 20);
                const boardingLabelHtml = (boardingLabelWrapped || '').replace(/\r?\n/g, '<br/>');

                const info = new window.kakao.maps.InfoWindow({
                  content:
                    `<div style="padding:5px 50px 10px 10px; font-family: inherit; position:relative; overflow: visible;">
                      <button id="closeBoardingInfoWindowBtn${i}" style="position:absolute; top:4px; right:4px; background:#e74c3c; color:#fff; border:none; border-radius:4px; width:22px; height:22px; font-size:14px; cursor:pointer;">×</button>` +
                    `<span style="font-size: 14px;white-space:nowrap; overflow:hidden; text-overflow:ellipsis; display:inline-block; max-width:180px; vertical-align:bottom;">${route.boarding.name}</span><br>>> ` +
                    `<strong style="color:${color}; font-size: 16px;">${route.routeName}</strong style="color:#000000;font-size: 14px;"> 승차<br>` +
                    (boardingLabelHtml ? `<span style="font-size: 14px; color: #666; display: block; white-space:nowrap;"> ${boardingLabelHtml}</span>` : '') +
                    `</div>`
                });

                let infoOpen = false;

                window.kakao.maps.event.addListener(marker, 'click', function() {
                  if (infoOpen) return;
                  info.open(map, marker);
                  marker.setMap(null);
                  indexOverlay.setMap(null); // 오버레이도 같이 숨김
                  infoOpen = true;
                  setTimeout(() => {
                    const btn = document.getElementById('closeBoardingInfoWindowBtn' + i);
                    if (btn) {
                      btn.onclick = function() {
                        info.close();
                        marker.setMap(map);
                        indexOverlay.setMap(map); // 오버레이도 같이 표시
                        infoOpen = false;
                      };
                    }
                  }, 0);
                });
                window.kakao.maps.event.addListener(info, 'close', function() {
                  if (!infoOpen) return;
                  marker.setMap(map);
                  indexOverlay.setMap(map);
                  infoOpen = false;
                });
                infoWindows.push(info);
              }
              if (alightingLat && alightingLng) {
                const marker = new window.kakao.maps.Marker({
                  position: new window.kakao.maps.LatLng(alightingLat, alightingLng),
                  map: map
                });
                markers.push({ marker });
                // InfoWindow 생성 및 이벤트만 유지 (오버레이 없음)
                const routeLabel = labels && labels[i] ? labels[i] : '';
                let alightingLabel = routeLabel;
                if (routeLabel.includes('//')) {
                  [, alightingLabel] = routeLabel.split('//', 2);
                }
                const alightingLabelWrapped = wrapTextByWord(alightingLabel, 20);
                const alightingLabelHtml = (alightingLabelWrapped || '').replace(/\r?\n/g, '<br/>');
                const info = new window.kakao.maps.InfoWindow({
                  content:
                    `<div style="padding:5px 50px 10px 10px; font-family: inherit; position:relative; overflow: visible;">
                      <button id="closeAlightingInfoWindowBtn${i}" style="position:absolute; top:4px; right:4px; background:#e74c3c; color:#fff; border:none; border-radius:4px; width:22px; height:22px; font-size:14px; cursor:pointer;">×</button>` +
                    `<strong style="color:${color}; font-size: 16px;"> ${route.routeName}</strong>` +
                    `<span style="color:black; font-size: 14px;"> 하차 >> </span><br>` +
                    `<span style="font-size: 14px;white-space:nowrap; overflow:hidden; text-overflow:ellipsis; display:inline-block; max-width:180px; vertical-align:bottom;">${route.alighting.name}</span><br>` +
                    (alightingLabelHtml ? `<span style="font-size: 14px; color: #666; display: block; white-space:nowrap;"> ${alightingLabelHtml}</span>` : '') +
                    `</div>`
                });
                let infoOpen = false;
                window.kakao.maps.event.addListener(marker, 'click', function() {
                  if (infoOpen) return;
                  info.open(map, marker);
                  marker.setMap(null);
                  infoOpen = true;
                  setTimeout(() => {
                    const btn = document.getElementById('closeAlightingInfoWindowBtn' + i);
                    if (btn) {
                      btn.onclick = function() {
                        info.close();
                        marker.setMap(map);
                        infoOpen = false;
                      };
                    }
                  }, 0);
                });
                window.kakao.maps.event.addListener(info, 'close', function() {
                  if (!infoOpen) return;
                  marker.setMap(map);
                  infoOpen = false;
                });
                infoWindows.push(info);
              }
            }
          }
        }

        // 지도 범위 조정
        if (polylines.length > 0) {
          const bounds = new window.kakao.maps.LatLngBounds();
          polylines.forEach(polyline => {
            const path = polyline.getPath();
            path.forEach(point => bounds.extend(point));
          });
          map.setBounds(bounds);
        }
      })();

      // cleanup
      return () => {
        polylines.forEach(p => p.setMap(null));
        markers.forEach(obj => {
          if (obj.marker) obj.marker.setMap(null);
          if (obj.overlay) obj.overlay.setMap(null);
        });
        infoWindows.forEach(iw => iw.close());
        arrows.forEach(a => a.setMap(null));
        isUnmounted = true;
      };
    });
  }, [open, routes, labels, zoom, showAllMarkers]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", flexDirection: "column"
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, margin: "auto", width: "100vw", height: "90vh", position: "relative"
      }}>
        <button onClick={() => {
          if (window.history.length > 2) {
            window.history.back();
          } else {
            window.location.hash = '/home';
          }
        }} style={{
          position: "absolute", right: 10, top: 20, zIndex: 10, fontSize: 16, background: "#e74c3c", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px"
        }}>닫기</button>
        <button
          onClick={() => {
            const url = window.location.origin + window.location.pathname + decodeURIComponent(window.location.hash);
            if (navigator.clipboard) {
              navigator.clipboard.writeText(url);
              alert('URL이 클립보드에 복사되었습니다!');
            } else {
              // fallback
              const textarea = document.createElement('textarea');
              textarea.value = url;
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand('copy');
              document.body.removeChild(textarea);
              alert('URL이 클립보드에 복사되었습니다!');
            }
          }}
          style={{
            position: "absolute", left: 10, top: 20, zIndex: 10, fontSize: 16, background: "#3498db", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px"
          }}
        >
          URL
        </button>
        <h2 style={{ textAlign: "center", margin: 0, padding: 16 }}>{title || "노선 지도"}</h2>
        <div ref={mapRef} id="map" style={{ width: "100%", height: "80vh", borderRadius: 8 }} />
        <div style={{ padding: 16, textAlign: "center" }}>
          {routes.map((route, i) => (
            <span key={i} style={{
              color: getRouteTypeColor(route.routeType),
              fontWeight: "bold", fontSize: 18, marginRight: 16
            }}>
              {route.routeName}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
